import {
	cancel,
	confirm,
	intro,
	isCancel,
	outro,
	select,
	text,
} from "@clack/prompts";
import { ACCOUNT_FIELDS, filterSpecByTag, nativeSpec } from "../api";
import {
	type AuthContext,
	activeAuthContext,
	hasRequiredOverrides,
	requiredOverrides,
} from "../api/required-overrides";
import { performLogin } from "../auth/login";
import { PromptCancelledError } from "../auth/prompts";
import { API_GROUPS } from "../api/groups";

/**
 * Human-mode interactive flow for missing required params: instead of letting
 * a spec command fail validation (or fall back to a confusing API default),
 * ask for the missing values with @clack/prompts and re-inject them as argv
 * tokens before the CLI serves the command. Agents (non-TTY) never enter this
 * flow — they get the structured validation error instead.
 */

interface SpecSchema {
	type?: string;
	enum?: unknown[];
	description?: string;
}

interface SpecParam {
	name?: string;
	in?: string;
	required?: boolean;
	description?: string;
	schema?: SpecSchema;
	$ref?: string;
}

export interface SpecCommand {
	method: string;
	path: string;
	parameters: SpecParam[];
	bodyProperties: Record<string, SpecSchema>;
	bodyRequired: string[];
}

export interface MissingParam {
	name: string;
	kind: "arg" | "option";
	description?: string | undefined;
	type: string;
	enumValues?: string[] | undefined;
}

const HTTP_METHODS = new Set([
	"get",
	"put",
	"post",
	"delete",
	"options",
	"head",
	"patch",
	"trace",
]);

const NON_EXECUTING_FLAGS = new Set([
	"--help",
	"-h",
	"--schema",
	"--llms",
	"--llms-full",
	"--version",
]);

const GLOBAL_VALUE_FLAGS = new Set([
	"format",
	"filter_output",
	"token_limit",
	"token_offset",
]);

const GLOBAL_BOOLEAN_FLAGS = new Set([
	"full_output",
	"json",
	"mcp",
	"token_count",
]);

const PROMPTABLE_TYPES = new Set(["string", "number", "integer", "boolean"]);

function normalizeName(name: string): string {
	return name.replace(/-/g, "_");
}

export function findSpecCommand(
	group: string,
	sub: string,
): SpecCommand | null {
	const entry = API_GROUPS.find(({ name }) => name === group);
	if (!entry) return null;
	const spec = filterSpecByTag(
		nativeSpec as Record<string, unknown>,
		entry.tag,
	);
	for (const [path, methods] of Object.entries(
		spec.paths as Record<string, Record<string, unknown>>,
	)) {
		for (const [method, op] of Object.entries(methods)) {
			if (!HTTP_METHODS.has(method)) continue;
			const operation = op as Record<string, unknown>;
			if (operation.operationId !== sub) continue;
			const body = (
				operation.requestBody as
					| {
							content?: Record<
								string,
								{
									schema?: {
										properties?: Record<string, SpecSchema>;
										required?: string[];
									};
								}
							>;
					  }
					| undefined
			)?.content?.["application/json"]?.schema;
			return {
				method,
				path,
				parameters: Array.isArray(operation.parameters)
					? (operation.parameters as SpecParam[])
					: [],
				bodyProperties: body?.properties ?? {},
				bodyRequired: body?.required ?? [],
			};
		}
	}
	return null;
}

function optionTakesValue(name: string, command: SpecCommand): boolean {
	for (const param of command.parameters) {
		if (param.name && normalizeName(param.name) === name) {
			return param.schema?.type !== "boolean";
		}
	}
	for (const [key, schema] of Object.entries(command.bodyProperties)) {
		if (normalizeName(key) === name) return schema.type !== "boolean";
	}
	return GLOBAL_VALUE_FLAGS.has(name);
}

interface ProvidedInput {
	options: Set<string>;
	positionals: string[];
}

function scanProvided(
	tokens: string[],
	command: SpecCommand,
	skipUnknownShortFlags = false,
): ProvidedInput | null {
	const options = new Set<string>();
	const positionals: string[] = [];
	for (let i = 0; i < tokens.length; i++) {
		const token = tokens[i] ?? "";
		if (token === "--") {
			positionals.push(...tokens.slice(i + 1));
			break;
		}
		if (token.startsWith("--")) {
			const eq = token.indexOf("=");
			let name = eq === -1 ? token.slice(2) : token.slice(2, eq);
			if (name.startsWith("no-")) name = name.slice(3);
			const normalized = normalizeName(name);
			options.add(normalized);
			if (eq === -1 && optionTakesValue(normalized, command)) i++;
		} else if (token.startsWith("-") && token.length > 1) {
			if (!skipUnknownShortFlags) return null;
		} else {
			positionals.push(token);
		}
	}
	return { options, positionals };
}

export function hasLegacyAccountId(argv: string[]): boolean {
	if (argv.some((token) => NON_EXECUTING_FLAGS.has(token))) return false;

	const commandArgv: string[] = [];
	for (let i = 0; i < argv.length; i++) {
		const token = argv[i] ?? "";
		if (token.startsWith("--") && !token.includes("=")) {
			const name = normalizeName(token.slice(2));
			if (GLOBAL_BOOLEAN_FLAGS.has(name)) continue;
			if (GLOBAL_VALUE_FLAGS.has(name)) {
				i++;
				continue;
			}
		}
		commandArgv.push(token);
	}

	const [group, sub, ...rest] = commandArgv;
	if (group !== "accounts" || !sub) return false;

	const command = findSpecCommand(group, sub);
	if (!command || !command.path.includes("{account_id}")) return false;

	return (scanProvided(rest, command, true)?.positionals.length ?? 0) > 0;
}

function toMissingParam(
	name: string,
	kind: "arg" | "option",
	schema: SpecSchema | undefined,
	description?: string,
): MissingParam | null {
	const enumValues = schema?.enum?.every((v) => typeof v === "string")
		? (schema.enum as string[])
		: undefined;
	const type = schema?.type ?? "string";
	if (!enumValues && !PROMPTABLE_TYPES.has(type)) return null;
	return {
		name,
		kind,
		description: description ?? schema?.description,
		type,
		enumValues,
	};
}

/**
 * Returns the required params the invocation is missing, in prompt order
 * (positional args first). Returns null when the invocation can't be safely
 * analyzed or a missing param isn't promptable — callers should then fall
 * through to incur's standard validation error.
 */
export function collectMissingParams(
	command: SpecCommand,
	tokens: string[],
	context: AuthContext | null,
): MissingParam[] | null {
	const provided = scanProvided(tokens, command);
	if (!provided) return null;

	const missing: MissingParam[] = [];

	const pathParams = command.parameters.filter((p) => p.in === "path");
	for (const param of pathParams.slice(provided.positionals.length)) {
		if (!param.name) return null;
		const missingParam = toMissingParam(
			param.name,
			"arg",
			param.schema,
			param.description,
		);
		if (!missingParam) return null;
		missing.push(missingParam);
	}

	const seen = new Set<string>();
	const addOption = (
		name: string,
		schema: SpecSchema | undefined,
		description?: string,
	): boolean => {
		const normalized = normalizeName(name);
		if (seen.has(normalized) || provided.options.has(normalized)) return true;
		seen.add(normalized);
		const missingParam = toMissingParam(name, "option", schema, description);
		if (!missingParam) return false;
		missing.push(missingParam);
		return true;
	};

	for (const param of command.parameters) {
		if (param.in !== "query" || !param.required || !param.name) continue;
		if (ACCOUNT_FIELDS.includes(param.name)) continue;
		if (!addOption(param.name, param.schema, param.description)) return null;
	}

	for (const name of command.bodyRequired) {
		if (ACCOUNT_FIELDS.includes(name)) continue;
		if (!addOption(name, command.bodyProperties[name])) return null;
	}

	if (context) {
		for (const override of requiredOverrides(
			command.method,
			command.path,
			context,
		)) {
			if (
				!addOption(
					override.name,
					command.bodyProperties[override.name],
					override.prompt,
				)
			) {
				return null;
			}
		}
	}

	return missing;
}

function promptMessage(param: MissingParam): string {
	const summary = param.description?.split(/\.\s|\n/)[0]?.trim();
	if (!summary) return param.name;
	const truncated =
		summary.length > 100 ? `${summary.slice(0, 97)}...` : summary;
	return `${param.name} — ${truncated}`;
}

async function promptForParam(param: MissingParam): Promise<string[] | null> {
	if (param.enumValues) {
		const value = await select<string>({
			message: promptMessage(param),
			options: param.enumValues.map((v) => ({ value: v, label: v })),
		});
		if (isCancel(value)) return null;
		return param.kind === "arg" ? [value] : [`--${param.name}`, value];
	}

	if (param.type === "boolean") {
		const value = await confirm({ message: promptMessage(param) });
		if (isCancel(value)) return null;
		return [value ? `--${param.name}` : `--no-${param.name}`];
	}

	const numeric = param.type === "number" || param.type === "integer";
	const value = await text({
		message: promptMessage(param),
		validate: (input) => {
			if (!input || input.trim().length === 0)
				return `${param.name} is required`;
			if (numeric && !/^-?\d+(\.\d+)?$/.test(input.trim()))
				return `${param.name} must be a number`;
			return undefined;
		},
	});
	if (isCancel(value)) return null;
	const trimmed = value.trim();
	return param.kind === "arg" ? [trimmed] : [`--${param.name}`, trimmed];
}

async function resolveContext(
	command: SpecCommand,
): Promise<AuthContext | null> {
	const context = activeAuthContext();
	if (context || !hasRequiredOverrides(command.method, command.path)) {
		return context;
	}

	let result: Awaited<ReturnType<typeof performLogin>>;
	try {
		result = await performLogin({ agent: false });
	} catch (err) {
		if (err instanceof PromptCancelledError) {
			cancel("Login cancelled.");
			process.exit(130);
		}
		throw err;
	}
	if (!result.ok) {
		cancel(result.message);
		process.exit(1);
	}
	return activeAuthContext();
}

export async function promptForMissingParams(
	argv: string[],
): Promise<string[]> {
	if (!process.stdin.isTTY || !process.stdout.isTTY) return argv;
	const [group, sub, ...rest] = argv;
	if (!group || !sub || sub.startsWith("-")) return argv;
	if (argv.some((token) => NON_EXECUTING_FLAGS.has(token))) return argv;

	const command = findSpecCommand(group, sub);
	if (!command) return argv;

	const context = await resolveContext(command);
	const missing = collectMissingParams(command, rest, context);
	if (!missing || missing.length === 0) return argv;

	intro(`whop ${group} ${sub}`);
	const extra: string[] = [];
	for (const param of missing) {
		const tokens = await promptForParam(param);
		if (!tokens) {
			cancel("Cancelled.");
			process.exit(130);
		}
		extra.push(...tokens);
	}
	outro("All set — running the command.");
	return [...argv, ...extra];
}
